import { compact, isEmpty, isNil } from 'lodash';
import type { TFunction } from 'react-i18next';
import type { AppDispatch } from '../../store';
import { isDateSmallerThanOrEqual, sameDay } from '../../utils/date';
import { MessageSubType, ReceivedMessageType, ReplyPurpose } from '../../utils/enums';
import { getImageLink } from '../../utils/formatImageLink';
import { translate } from '../../utils/translate';
import { readMessage, syncMessage } from '../ChatsList/chatListSlice/actionCreators';
import type { Chat, Reply } from '../ChatsList/chatListSlice/types';
import type { ReplyToMessageIcon, ReplyToMessageShorted } from './Chat';
import { palette } from '../../theme/colors';

export type ChecklistSubTypes = Exclude<MessageSubType, MessageSubType.personalAlarm>;

export const checklistMessageTitleTX: Record<ChecklistSubTypes, string> = {
  [MessageSubType.startedChecklist]: 'checklist_started',
  [MessageSubType.sharedChecklist]: 'checklist_shared',
  [MessageSubType.reactivatedChecklist]: 'checklist_reactivated',
};

export const fromPurposeChecklistMessageTitleTX: Record<number, string> = {
  [ReplyPurpose.ChecklistEnded]: 'checklist_started',
  [ReplyPurpose.ChecklistReactivated]: 'checklist_reactivated',
};

export const getMessageHeaderSubTitle = (
  message: Chat,
  t: TFunction<'translation', undefined>,
  seperator = ', '
): string => {
  if (message.type === 3) {
    //Broadcast
    const organizations = message?.organizations;
    const text =
      !isNil(organizations) && organizations.length > 1
        ? `${organizations.length} ${t('messages_title_info_label')}`
        : !isNil(organizations) && !isNil(organizations[0])
          ? organizations[0].name
          : '';
    if (!isEmpty(text) && !isNil(text)) {
      return text;
    }
  } else if (message.type === 10) {
    return message.senderName;
  }
  const groupsCount = message.groupIDs?.length || message.groupNames?.length;
  const recipentsCount = message.recipientCount;
  let groupsCountText = '';
  let recipentsCountText = '';
  if (groupsCount) {
    groupsCountText =
      groupsCount > 1 ? `${groupsCount} ${t('groups_title')}` : `${groupsCount} ${t('group')}`;
  }
  if (recipentsCount) {
    recipentsCountText =
      recipentsCount > 1
        ? `${recipentsCount} ${t(`groups_members`)}`
        : `${recipentsCount} ${t('member')}`;
  }

  return compact([groupsCountText, recipentsCountText]).join(seperator);
};

export const checkHasReply = (_reply: Reply): boolean => {
  const { replyId, replyToInitialMessage } = _reply;
  if (replyToInitialMessage) return true;
  if (replyId === undefined || replyId === null) return false;
  return true;
};

export const generateReplyToMessageBodyText = (_message: Chat | Reply, isReply = false): string => {
  if (isReply) {
    const replyData = _message as Reply;
    const initialReplyPhotoFileNamesCount = replyData.initialReplyPhotoFileNames
      ? new Set(replyData.initialReplyPhotoFileNames).size
      : 0;
    const initialReplyDocumentsFileNamesCount = replyData.initialReplyDocumentFileNames
      ? new Set(replyData.initialReplyDocumentFileNames).size
      : 0;

    if (replyData.initialReplyLocation) return translate('messages_location');
    else if (
      replyData.initialReplyText &&
      !initialReplyDocumentsFileNamesCount &&
      !initialReplyPhotoFileNamesCount
    )
      return replyData.initialReplyText;
    else if (initialReplyPhotoFileNamesCount)
      return translate(initialReplyPhotoFileNamesCount > 1 ? 'photos' : 'photo');
    else if (initialReplyDocumentsFileNamesCount)
      return translate(
        initialReplyDocumentsFileNamesCount > 1 ? 'documents_documents' : 'documents_document'
      );
    else if (replyData.initialReplyAudioFileNames?.length) return translate('messages_voicemail');
    else return translate('message_a_deleted');
  } else {
    const photosCount = _message.photoFileNames?.length || 0;
    const documentsCount = _message.documentFileNames?.length || 0;

    if (_message.type === 2) return _message.emergencyTypeName || '';
    else if (_message.locationID && _message.type !== 2) return translate('messages_location');
    else if (!photosCount && _message.text && !documentsCount) return _message.text;
    else if (_message.photoFileNames?.length !== 0) return translate('photo');
    else if (_message.documentFileNames?.length !== 0) return translate('documents_documents');
    else if (_message.audioFileNames) return translate('messages_voicemail');
    else return translate('message_a_deleted');
  }
};

export const generateReplyToMessageIcon = (
  _message: Chat | Reply,
  isReply = false
): ReplyToMessageIcon | undefined => {
  if (isReply) {
    const replyData = _message as Reply;
    if (replyData.initialReplyPhotoFileNames?.length)
      return {
        src: getImageLink({
          imageName: replyData.initialReplyPhotoFileNames[0],
          size: 'small',
        }),
        classPresets: 'photoMessageIcon',
      };
  } else {
    if (_message.type === 2 && _message.emergencyTypeIconFileName)
      return {
        src: getImageLink({ imageName: _message?.emergencyTypeIconFileName }),
        classPresets: 'alarmMessageIcon',
      };
    if (_message.photoFileNames && _message.photoFileNames.length !== 0)
      return {
        src: getImageLink({
          imageName: _message.photoFileNames[0],
          size: 'small',
        }),
        classPresets: 'photoMessageIcon',
      };
  }
  return undefined;
};

export const generateMessageReplyToMessage = (
  _reply: Reply,
  message: Chat
): ReplyToMessageShorted | undefined => {
  const { senderName, id } = message;
  const { replyId, replyToInitialMessage } = _reply;
  if (replyToInitialMessage) {
    // reply to main message
    return (
      message && {
        messageIcon: generateReplyToMessageIcon(message),
        messageText: generateReplyToMessageBodyText(message),
        senderName: senderName,
        messageId: id,
        isReplyToInitialMessage: true,
      }
    );
  }
  // no reply
  if (replyId === undefined || replyId === null) return undefined;
  if (replyId !== undefined && replyId !== null) {
    return {
      messageIcon: generateReplyToMessageIcon(_reply, true),
      messageText: generateReplyToMessageBodyText(_reply, true),
      senderName: _reply.initialReplySender || '',
      messageId: replyId,
      isReplyToInitialMessage: false,
      isDeleted: _reply.isInitialReplyDeleted ?? false,
    };
  }
};

const getDateFormat = (date: string) => {
  return new Date(date.replace(' ', 'T') + 'Z');
};

export const isReplyInSameDay = (
  message: Chat,
  reply: Reply,
  replyIndex: number,
  replies?: Reply[]
): boolean => {
  const thisReplyDate = getDateFormat(reply.sent);
  const messageSentDate = getDateFormat(message.sent);
  const repliesList = replies || message.replies;
  const prevReplyDate = replyIndex > 0 && getDateFormat(repliesList[replyIndex - 1].sent);
  const isSameDay = prevReplyDate
    ? sameDay(prevReplyDate, thisReplyDate)
    : sameDay(messageSentDate, thisReplyDate);
  return isSameDay;
};

// Deleted, alarm-recall, checklist and (un)block replies render as separators or
// cards instead of sender bubbles, so they break a consecutive-bubble group.
const isBubbleReply = (reply: Reply): boolean =>
  !reply.deleted &&
  !reply.emergencyRecall &&
  !reply.checklistID &&
  reply.purpose !== ReplyPurpose.blockedComment &&
  reply.purpose !== ReplyPurpose.unBlockedComment;

export const isTheSameReplySender = (
  message: Chat,
  reply: Reply,
  replyIndex: number,
  replies?: Reply[]
): boolean => {
  const repliesList = replies || message.replies;
  const prevReply = replyIndex > 0 ? repliesList[replyIndex - 1] : undefined;
  if (prevReply && !isBubbleReply(prevReply)) return false;

  const prevReplySenderID = prevReply ? prevReply.senderID : message.senderID;
  return (
    reply.senderID === prevReplySenderID && isReplyInSameDay(message, reply, replyIndex, replies)
  );
};

// The replies list is oldest-first, so the message rendered below the one at
// `replyIndex` (the next one in reading order) lives at `replyIndex + 1`. Passing
// `-1` as `replyIndex` checks the main message against the oldest reply.
export const isTheSameReplySenderAsNext = (
  message: Chat,
  replyIndex: number,
  replies?: Reply[]
): boolean => {
  const repliesList = replies || message.replies;
  const nextReply = repliesList[replyIndex + 1];
  if (!nextReply || !isBubbleReply(nextReply)) return false;

  return isTheSameReplySender(message, nextReply, replyIndex + 1, repliesList);
};

export const checkIfNeedRead = (currentChat: Chat, replies: Reply[], dispatch: AppDispatch) => {
  const replyIds = replies.map(reply => reply.id);
  const { id, sent, lastRead, lastReplySent } = currentChat;
  const lastReplySentTime = replies[replies.length - 1]?.sent ?? lastReplySent ?? sent;
  const shouldRead = !lastRead || isDateSmallerThanOrEqual(lastRead, lastReplySentTime);
  if (shouldRead) {
    dispatch(readMessage(id, replyIds));
  } else {
    dispatch(syncMessage(id, lastReplySentTime));
  }
};

export const removeDuplicatesByProperty = <T,>(items: T[], propertyName: keyof T): T[] => {
  const uniqueItems = new Map<unknown, T>();

  items.forEach(item => {
    const key = item[propertyName];
    uniqueItems.set(key, item);
  });

  return Array.from(uniqueItems.values());
};

export const getHeaderBackgroundColor = (message: Chat) => {
  const isActiveAlarm = message && message.type === ReceivedMessageType.Alarm && !message.recalled;
  if (!isActiveAlarm) return undefined;

  return palette.danger; // TODO After 3.13.0 release change it back to message.emergencyTypeColor ?? palette.danger
};
